import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { apiClient } from '@/api/client'
import { userApi, User } from '@/api/users'
import { callCenterApi } from '@/api/callcenter'
import { Button } from '@/components/ui/Button'
import { useAuthStore } from '@/store/auth'

interface NewCustomerForm {
  email: string
  password: string
  first_name: string
  last_name: string
  phone?: string
  date_of_birth?: string
  national_id?: string
  address?: string
  city?: string
  country?: string
  postal_code?: string
  // callcenter extras
  applied_bank?: string
  applied_amount?: number
  offered_interest_rate?: number
  application_website?: string
}

const formatCurrencyInput = (value: number | undefined): string => {
  if (value === undefined || value === null || isNaN(Number(value))) return ''
  return Number(value).toLocaleString('tr-TR', { maximumFractionDigits: 0 })
}
const parseCurrencyInput = (text: string): number | undefined => {
  if (!text) return undefined
  // remove non-digits except comma/dot
  const cleaned = text.replace(/\./g, '').replace(/\s/g, '').replace(/,/g, '')
  const n = Number(cleaned)
  return isNaN(n) ? undefined : n
}

const inputCls = "border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"

const CustomerAdmin: React.FC = () => {
  const { user } = useAuthStore()
  const teamId = (user as any)?.team_id as number | undefined

  const [customers, setCustomers] = useState<User[]>([])
  const [agents, setAgents] = useState<User[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)

  const [form, setForm] = useState<NewCustomerForm>({ email: '', password: '', first_name: '', last_name: '', phone: '', date_of_birth: '', national_id: '', address: '', city: '', country: '', postal_code: '', applied_bank: '', applied_amount: undefined, offered_interest_rate: undefined, application_website: '' })

  const filteredCustomers = useMemo(() => {
    if (!search) return customers
    const s = search.toLowerCase()
    return customers.filter(c => `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(s))
  }, [customers, search])

  const fetchLists = async () => {
    if (!teamId) return
    setLoading(true)
    try {
      const [custRes, agRes] = await Promise.all([
        apiClient.get('/api/v1/users', { params: { role: 'customer', page_size: 100, team_id: teamId, is_active: true } }),
        apiClient.get('/api/v1/users', { params: { role: 'staff', page_size: 100, team_id: teamId } }),
      ])
      setCustomers(custRes.data?.users || [])
      setAgents(agRes.data?.users || [])
    } catch (e:any) {
      toast.error(e?.message || 'Listeler getirilemedi')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchLists() }, [teamId])

  const createCustomer = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!teamId) { toast.error('Takım bilgisi yok'); return }
    try {
      const payload: any = { ...form, role: 'customer', is_active: true, team_id: teamId }
      if (!form?.date_of_birth) delete payload.date_of_birth
      if (!form?.national_id) delete payload.national_id
      if (!form?.address) delete payload.address
      if (!form?.city) delete payload.city
      if (!form?.country) delete payload.country
      if (!form?.postal_code) delete payload.postal_code
      if (!form?.applied_bank) delete payload.applied_bank
      if (!form?.applied_amount) delete payload.applied_amount
      if (!form?.offered_interest_rate) delete payload.offered_interest_rate
      if (!form?.application_website) delete payload.application_website
      const newUser = await userApi.create(payload)
      toast.success('Müşteri oluşturuldu')
      setForm({ email: '', password: '', first_name: '', last_name: '', phone: '', date_of_birth: '', national_id: '', address: '', city: '', country: '', postal_code: '' })
      await fetchLists()
    } catch (e:any) { toast.error(e?.message || 'Müşteri oluşturulamadı') }
  }

  const [editUser, setEditUser] = useState<User | null>(null)
  const [editData, setEditData] = useState<{ first_name?: string; last_name?: string; phone?: string; password?: string; date_of_birth?: string; national_id?: string; address?: string; city?: string; country?: string; postal_code?: string; applied_bank?: string; applied_amount?: number; offered_interest_rate?: number; application_website?: string }>({})
  const openEdit = (u: User) => {
    const x: any = u as any
    setEditUser(u)
    setEditData({
      first_name: u.first_name,
      last_name: u.last_name,
      phone: x.phone || '',
      date_of_birth: x.date_of_birth || '',
      national_id: x.national_id || '',
      address: x.address || '',
      city: x.city || '',
      country: x.country || '',
      postal_code: x.postal_code || '',
      applied_bank: x.applied_bank || '',
      applied_amount: x.applied_amount ?? undefined,
      offered_interest_rate: x.offered_interest_rate ?? undefined,
      application_website: x.application_website || ''
    })
  }
  const saveEdit = async () => {
    if (!editUser) return
    try {
      const payload: any = {}
      Object.entries(editData).forEach(([k, v]) => {
        if (k === 'password') return
        if (v !== undefined && v !== '') payload[k] = v
      })
      await userApi.update(editUser.id, payload)
      if (editData.password && editData.password.trim().length >= 8) {
        await apiClient.post(`/api/v1/users/${editUser.id}/set-password`, { new_password: editData.password })
      }
      toast.success('Müşteri güncellendi')
      setEditUser(null)
      await fetchLists()
    } catch (e:any) { toast.error(e?.message || 'Güncellenemedi') }
  }

  const removeCustomer = async (u: User) => {
    if (!confirm('Müşteri silinecek, emin misiniz?')) return
    try {
      await userApi.delete(u.id)
      toast.success('Müşteri silindi')
      await fetchLists()
    } catch (e:any) { toast.error(e?.message || 'Silinemedi') }
  }

  const [assignTarget, setAssignTarget] = useState<{ customerId: number | ''; agentId: number | '' }>({ customerId: '', agentId: '' })
  const assign = async () => {
    if (!assignTarget.customerId || !assignTarget.agentId) { toast.error('Müşteri ve personel seçin'); return }
    try {
      await callCenterApi.assignments.create(Number(assignTarget.customerId), Number(assignTarget.agentId))
      toast.success('Atama yapıldı')
      setAssignTarget({ customerId: '', agentId: '' })
      await fetchAssignments()
    } catch (e:any) { toast.error(e?.message || 'Atama başarısız') }
  }

  const [assignments, setAssignments] = useState<any[]>([])
  const fetchAssignments = async () => {
    try {
      const res = await callCenterApi.assignments.list({ is_active: true })
      setAssignments(res)
    } catch { /* ignore */ }
  }
  useEffect(() => { fetchAssignments() }, [])

  const unassign = async (customerId: number) => {
    try {
      await callCenterApi.assignments.unassign(customerId)
      toast.success('Atama kaldırıldı')
      await fetchAssignments()
    } catch (e:any) { toast.error(e?.message || 'Atama kaldırılamadı') }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="pb-5 border-b border-slate-200">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-1">Callcenter</p>
        <h1 className="text-2xl font-serif font-medium text-slate-900">Müşteri Yönetimi</h1>
        <p className="text-slate-600 mt-1">Takım müşterilerini yönetin, atamalar yapın</p>
      </div>

      {/* Create Customer */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">Yeni Müşteri Oluştur</h2>
        <form onSubmit={createCustomer} className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <input className={inputCls + " md:col-span-2"} placeholder="Ad" value={form.first_name} onChange={e=>setForm({...form, first_name: e.target.value})} required />
          <input className={inputCls + " md:col-span-2"} placeholder="Soyad" value={form.last_name} onChange={e=>setForm({...form, last_name: e.target.value})} required />
          <input className={inputCls + " md:col-span-2"} placeholder="Telefon" value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} />
          <input className={inputCls + " md:col-span-3"} placeholder="Email" type="email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} required />
          <input className={inputCls + " md:col-span-3"} placeholder="Geçici Şifre" type="password" value={form.password} onChange={e=>setForm({...form, password: e.target.value})} required />
          <input className={inputCls + " md:col-span-2"} placeholder="Doğum Tarihi" type="date" value={form.date_of_birth || ''} onChange={e=>setForm({...form, date_of_birth: e.target.value})} />
          <input className={inputCls + " md:col-span-2"} placeholder="TC Kimlik No / National ID" value={form.national_id || ''} onChange={e=>setForm({...form, national_id: e.target.value})} />
          <input className={inputCls + " md:col-span-2"} placeholder="Şehir" value={form.city || ''} onChange={e=>setForm({...form, city: e.target.value})} />
          <input className={inputCls + " md:col-span-3"} placeholder="Ülke" value={form.country || ''} onChange={e=>setForm({...form, country: e.target.value})} />
          <input className={inputCls + " md:col-span-3"} placeholder="Posta Kodu" value={form.postal_code || ''} onChange={e=>setForm({...form, postal_code: e.target.value})} />
          {/* Callcenter extras */}
          <input className={inputCls + " md:col-span-3"} placeholder="Yatırım için Başvurulan banka" value={form.applied_bank || ''} onChange={e=>setForm({...form, applied_bank: e.target.value})} />
          <input className={inputCls + " md:col-span-3"} placeholder="Yatırım için Başvurulan tutar" value={formatCurrencyInput(form.applied_amount)} onChange={e=>setForm({...form, applied_amount: parseCurrencyInput(e.target.value)})} inputMode="numeric" />
          <input className={inputCls + " md:col-span-3"} placeholder="Yatırım için verilen faiz teklifi (%)" value={form.offered_interest_rate ?? ''} onChange={e=>setForm({...form, offered_interest_rate: Number(e.target.value || 0)})} type="number" step="0.01" />
          <input className={inputCls + " md:col-span-3"} placeholder="Yatırım başvurulduğu websitesi" value={form.application_website || ''} onChange={e=>setForm({...form, application_website: e.target.value})} />
          <textarea className={inputCls + " md:col-span-6"} placeholder="Adres" rows={2} value={form.address || ''} onChange={e=>setForm({...form, address: e.target.value})} />
          <div className="md:col-span-6"><Button type="submit">Oluştur</Button></div>
        </form>
      </div>

      {/* Customers List */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Müşteriler</h2>
          <input className={inputCls} placeholder="Ara..." value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        {filteredCustomers.length === 0 ? (
          <p className="text-slate-500">Kayıt yok</p>
        ) : (
          <div className="space-y-2">
            {filteredCustomers.map(c => (
              <div key={c.id} className="p-3 border border-slate-200 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-medium text-slate-900">{c.first_name} {c.last_name}</p>
                  <p className="text-xs text-slate-500">{c.email}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select className={inputCls} value={assignTarget.customerId === c.id ? assignTarget.agentId : ''} onChange={e=>setAssignTarget({ customerId: c.id, agentId: Number(e.target.value) })}>
                    <option value="">Personel seç</option>
                    {agents.map(a => (<option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>))}
                  </select>
                  <Button variant="outline" onClick={assign}>Ata</Button>
                  <Button variant="outline" onClick={()=>openEdit(c)}>Düzenle</Button>
                  <Button variant="outline" className="text-danger-600" onClick={()=>removeCustomer(c)}>Sil</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assignments */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Aktif Atamalar</h2>
          <Button variant="outline" onClick={fetchAssignments}>Yenile</Button>
        </div>
        {assignments.length === 0 ? (
          <p className="text-slate-500">Kayıt yok</p>
        ) : (
          <div className="space-y-2">
            {assignments.map(a => (
              <div key={a.id} className="p-3 border border-slate-200 flex items-center justify-between">
                <div>
                  {(() => {
                    const cust = customers.find(c => c.id === a.customer_id)
                    const ag = agents.find(x => x.id === a.agent_id)
                    const custName = cust ? `${cust.first_name} ${cust.last_name}`.trim() : `#${a.customer_id}`
                    const agName = ag ? `${ag.first_name} ${ag.last_name}`.trim() : `#${a.agent_id}`
                    return (
                      <p className="font-medium text-slate-900">Müşteri: {custName} (ID: {a.customer_id}) → Personel: {agName} (ID: {a.agent_id})</p>
                    )
                  })()}
                  <p className="text-xs text-slate-500">{a.assigned_at}</p>
                </div>
                <Button variant="outline" className="text-danger-600" onClick={()=>unassign(a.customer_id)}>Kaldır</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal (simple inline) */}
      {editUser && (
        <div className="fixed inset-0 bg-primary-950/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 border-t-2 border-t-primary-600 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Müşteri Düzenle</h3>
            <input className={inputCls + " w-full"} placeholder="Ad" value={editData.first_name || ''} onChange={e=>setEditData({...editData, first_name: e.target.value})} />
            <input className={inputCls + " w-full"} placeholder="Soyad" value={editData.last_name || ''} onChange={e=>setEditData({...editData, last_name: e.target.value})} />
            <input className={inputCls + " w-full"} placeholder="Telefon" value={editData.phone || ''} onChange={e=>setEditData({...editData, phone: e.target.value})} />
            <input className={inputCls + " w-full"} type="date" placeholder="Doğum Tarihi" value={editData.date_of_birth || ''} onChange={e=>setEditData({...editData, date_of_birth: e.target.value})} />
            <input className={inputCls + " w-full"} placeholder="TC Kimlik No / National ID" value={editData.national_id || ''} onChange={e=>setEditData({...editData, national_id: e.target.value})} />
            <textarea className={inputCls + " w-full"} rows={2} placeholder="Adres" value={editData.address || ''} onChange={e=>setEditData({...editData, address: e.target.value})} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input className={inputCls} placeholder="Şehir" value={editData.city || ''} onChange={e=>setEditData({...editData, city: e.target.value})} />
              <input className={inputCls} placeholder="Ülke" value={editData.country || ''} onChange={e=>setEditData({...editData, country: e.target.value})} />
              <input className={inputCls} placeholder="Posta Kodu" value={editData.postal_code || ''} onChange={e=>setEditData({...editData, postal_code: e.target.value})} />
            </div>
            {/* Callcenter extras in edit modal */}
            <input className={inputCls + " w-full"} placeholder="Yatırım için Başvurulan banka" value={editData.applied_bank || ''} onChange={e=>setEditData({...editData, applied_bank: e.target.value})} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input className={inputCls} placeholder="Başvurulan tutar" value={formatCurrencyInput(editData.applied_amount)} onChange={e=>setEditData({...editData, applied_amount: parseCurrencyInput(e.target.value)})} inputMode="numeric" />
              <input className={inputCls} placeholder="Faiz teklifi (%)" value={editData.offered_interest_rate ?? ''} onChange={e=>setEditData({...editData, offered_interest_rate: Number(e.target.value || 0)})} type="number" step="0.01" />
            </div>
            <input className={inputCls + " w-full"} placeholder="Başvurulan website" value={editData.application_website || ''} onChange={e=>setEditData({...editData, application_website: e.target.value})} />
            <input className={inputCls + " w-full"} placeholder="Yeni Şifre (opsiyonel)" type="password" value={editData.password || ''} onChange={e=>setEditData({...editData, password: e.target.value})} />
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" onClick={()=>setEditUser(null)}>İptal</Button>
              <Button onClick={saveEdit}>Kaydet</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomerAdmin
